// Skeleton placeholder that mirrors the result's first screen block for
// block (layout B, 2026-10-29): the answer card (the photo tile beside the
// listing line on phones, the large photo with its thumbnails on the left
// from lg; the tier chip, the number, the gauge, the sentence, the asking
// / AI / difference row, the actions), then "Niyə?" beside the price
// comparison, and the "Ətraflı təhlil" row, so the loading -> result swap
// has no layout shift. Keep it in step with the dashboard (owner,
// 2026-10-18). Shown on the analyze page while the API is in flight and
// during the ResultCard chunk load.

import { SkeletonLine, SkeletonCircle } from "./Skeleton";

// The Skeleton pieces' surface, for shapes they do not draw (their block
// is always rounded-2xl and full width).
const PULSE = "animate-pulse bg-gray-200 dark:bg-white/10";

export default function ResultCardSkeleton() {
  return (
    <div className="space-y-5 sm:space-y-6">
      {/* The answer card. */}
      <div className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5 lg:p-6">
        <div className="grid grid-cols-[96px_minmax(0,1fr)] gap-x-3 gap-y-4 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] lg:gap-x-8">
          <div className="col-start-1 row-start-1 min-w-0">
            <div className={`${PULSE} h-24 w-24 rounded-xl lg:aspect-[4/3] lg:h-auto lg:w-full`} />
            <div className="mt-2 hidden gap-2 lg:flex">
              {[0, 1, 2, 3, 4].map((i) => (
                <div key={i} className={`${PULSE} h-16 w-[84px] shrink-0 rounded-lg`} />
              ))}
            </div>
          </div>
          <div className="max-lg:contents lg:col-start-2 lg:row-start-1 lg:flex lg:min-w-0 lg:flex-col lg:gap-5">
            <div className="col-start-2 row-start-1 min-w-0 space-y-2">
              <SkeletonLine width="w-4/5" className="h-4" />
              <SkeletonLine width="w-1/2" className="h-3" />
              <SkeletonLine width="w-16" className="h-3" />
            </div>
            <div className="col-span-2 min-w-0 lg:col-span-1">
              <div className="flex items-center justify-between gap-3">
                <SkeletonLine width="w-20" className="h-3" />
                <SkeletonLine width="w-16" className="h-4 rounded-full" />
              </div>
              <SkeletonLine width="w-32" className="mt-3 h-6 rounded-full" />
              <SkeletonLine width="w-3/5" className="mt-3 h-10" />
              <SkeletonLine width="w-24" className="mt-2 h-3" />
              <SkeletonLine width="w-full" className="mt-4 h-2 rounded-full" />
              <SkeletonLine width="w-4/5" className="mt-4 h-3" />
              <div className="mt-3 grid grid-cols-3 gap-3 border-t border-gray-100 pt-3">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="space-y-2">
                    <SkeletonLine width="w-16" className="h-2.5" />
                    <SkeletonLine width="w-3/4" className="h-5" />
                    <SkeletonLine width="w-1/2" className="h-2.5" />
                  </div>
                ))}
              </div>
              <SkeletonLine width="w-1/2" className="mt-3 h-3" />
            </div>
            {/* The action row: round icons with labels on phones, buttons from lg. */}
            <div className="col-span-2 min-w-0 border-t border-gray-100 pt-3 lg:col-span-1 lg:pt-4">
              <div className="grid grid-cols-5 gap-1 lg:flex lg:flex-wrap lg:gap-2">
                {[0, 1, 2, 3, 4].map((i) => (
                  <div key={i} className="flex flex-col items-center gap-1.5 lg:block">
                    <SkeletonCircle size="w-10 h-10" className="lg:hidden" />
                    <SkeletonLine width="w-12" className="h-2.5 lg:hidden" />
                    <div className={`${PULSE} hidden h-10 rounded-lg lg:block ${i === 4 ? "w-10" : "w-32"}`} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* "Diqqət edin" and "Niyə?" on the left, the price comparison on the
          right (one column on phones). */}
      <div className="flex flex-col gap-4 sm:gap-5 lg:grid lg:grid-cols-2 lg:items-start">
        <div className="flex min-w-0 flex-col gap-4 sm:gap-5">
          <div className="flex items-center gap-2 px-1">
            <SkeletonCircle size="w-4 h-4" />
            <SkeletonLine width="w-40" className="h-3" />
          </div>
          <div className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5">
            <SkeletonLine width="w-16" className="h-4" />
            <SkeletonLine width="w-full" className="mt-3 h-3" />
            <SkeletonLine width="w-3/4" className="mt-2 h-3" />
            <div className="mt-2 divide-y divide-gray-100">
              {[0, 1, 2].map((i) => (
                <div key={i} className="space-y-2 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <SkeletonLine width="w-28" className="h-3" />
                    <SkeletonLine width="w-12" className="h-3" />
                  </div>
                  <SkeletonLine width="w-2/3" className="h-2.5" />
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white px-4 py-4 sm:px-5">
          <SkeletonLine width="w-32" className="h-4" />
          <div className="mt-2 divide-y divide-gray-100">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex items-center justify-between gap-4 py-2.5">
                <div className="space-y-1.5">
                  <SkeletonLine width="w-28" className="h-3" />
                  {i === 1 && <SkeletonLine width="w-40" className="h-2.5" />}
                </div>
                <SkeletonLine width="w-20" className="h-3" />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* "Ətraflı təhlil". */}
      <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3.5 sm:px-5 sm:py-4">
        <SkeletonLine width="w-32" className="h-4" />
        <SkeletonLine width="w-3/4" className="mt-2 h-3" />
      </div>
    </div>
  );
}
